import Popover from '@components/Common/Popover.js';
import Search from '@components/Common/Search.js';
import toast from '@components/Common/Toast.js';
import { UserCircleIcon } from '@icons/index.js';
import { UIDiv, UIElement, UIImage, UIText } from '@ui';
import { updateColumnValue } from '@utils/columnUtils.js';

export default class PeopleCell extends UIDiv {
  constructor({
    column,
    item = {},
    value = '',
    canEdit = true,
    onValueChange = null,
    customStyle = '',
    members = [],
  }) {
    super();

    this.column = column;
    this.item = item;
    this.value = value;
    this.isEditMode = false;
    this.canEdit = canEdit;
    this.onValueChange = onValueChange; // Callback for value changes instead of API calls
    this.users = members || []; // Initialize empty users array
    this.selectedUsers = []; // Track selected users
    this.filteredUsers = [...this.users]; // Track filtered users for search
    this.customStyle = customStyle;

    if (this.customStyle !== '') this.setClass(`${this.customStyle}`);

    this.#updateSelectedUsers();
    this.render();
  }

  #updateSelectedUsers() {
    try {
      // Ensure this.value is a valid JSON array string
      const selectedUserIds = JSON.parse(this.value || '[]');
      if (!Array.isArray(selectedUserIds)) {
        this.selectedUsers = [];
        return;
      }
      // Filter the users provided in the constructor
      this.selectedUsers = this.users.filter(user =>
        selectedUserIds.includes(user.uid),
      );
    } catch (error) {
      console.error('Error parsing people value:', error);
      this.selectedUsers = [];
    }
  }

  render() {
    this.clear();

    if (!this.canEdit) {
      this.#renderUneditableInput();
      return;
    }

    // Update selected users based on current value
    this.#updateSelectedUsers();

    // Create the trigger element based on state
    const trigger =
      this.selectedUsers.length === 0
        ? this.#createEmptyTrigger()
        : this.#createSelectedTrigger();

    // Store reference to trigger for dynamic updates
    this.currentTrigger = trigger;

    // Create popover content
    const popoverContent = this.#createPopoverContent();
    trigger.onClick(e => {
      e.stopPropagation();
      Popover.show({
        trigger: trigger,
        content: popoverContent,
        placement: 'bottom-start',
        hideOnSelect: false, // Don't hide on select for multi-select
        hideOnScroll: true,
      });
    });

    trigger.setAttribute('tabindex', '0');
    trigger.onKeyDown(event => {
      const isEditable =
        this.canEdit && (!this.item?.isDefault || this.column.isCustom);
      if (event.key === 'Enter' && !this.isEditMode && isEditable) {
        event.preventDefault();
        trigger.dom.click();
      }
    });

    this.add(trigger);
  }

  /**
   * Creates the trigger element for when no users are selected.
   * @returns {UIDiv} The trigger element.
   * @private
   */
  #createEmptyTrigger() {
    const emptyContainer = new UIDiv();
    emptyContainer.addClass(
      'group hover:bg-gray-50 rounded-md min-h-[34px] cursor-pointer flex items-center justify-center',
    );

    // Always show user icon
    const userIcon = new UserCircleIcon({
      size: 20,
      color: 'none',
      strokeColor: '#525358',
    });
    emptyContainer.add(userIcon);

    return emptyContainer;
  }

  /**
   * Creates the trigger element for when users are selected.
   * @returns {UIDiv} The trigger element.
   * @private
   */
  #createSelectedTrigger() {
    const peopleContainer = new UIDiv();
    peopleContainer.addClass(
      'group hover:bg-gray-50 rounded-md min-h-[34px] cursor-pointer flex items-center justify-center gap-1 px-1 overflow-hidden',
    );

    // Show first 3 user avatars
    this.selectedUsers.slice(0, 3).forEach(user => {
      const avatar = this.#createUserAvatar(user, true); // true for removable
      peopleContainer.add(avatar);
    });

    // Show "+X more" indicator if there are more than 3 users
    if (this.selectedUsers.length > 3) {
      const moreIndicator = new UIDiv();
      moreIndicator.addClass(
        'w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-600 font-medium',
      );
      moreIndicator.setTextContent(`+${this.selectedUsers.length - 3}`);
      peopleContainer.add(moreIndicator);
    }

    return peopleContainer;
  }

  /**
   * Creates a user avatar with optional remove functionality
   * @param {Object} user - The user object
   * @param {boolean} removable - Whether to show remove button on hover
   * @returns {UIDiv} The avatar element
   * @private
   */
  #createUserAvatar(user, removable = false) {
    const avatarContainer = new UIDiv();
    avatarContainer.addClass(
      'relative w-6 h-6 rounded-full flex-shrink-0 group/avatar hover:scale-110 transition-transform cursor-pointer',
    );

    if (user.img_url) {
      const avatar = new UIImage(user.img_url);
      avatar.addClass('w-full h-full rounded-full object-cover');
      avatarContainer.add(avatar);
    } else {
      avatarContainer.addClass('bg-gray-200 flex items-center justify-center');
      const initials = user.full_name
        ? user.full_name.charAt(0).toUpperCase()
        : '?';
      const initialsText = new UIText(initials);
      initialsText.addClass('text-xs text-gray-600 font-medium');
      avatarContainer.add(initialsText);
    }

    // Add cross button for removal - only show on hover and if removable
    if (removable) {
      const removeButton = new UIDiv();
      removeButton.addClass(
        'absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity cursor-pointer hover:bg-red-600',
      );
      removeButton.setTextContent('×');
      removeButton.addClass('text-white text-xs font-bold');

      removeButton.onClick(e => {
        e.stopPropagation();
        this.#removeUser(user.uid);
      });

      avatarContainer.add(removeButton);
    }

    // Add tooltip - TODO
    avatarContainer.dom.title = user.full_name || user.email;

    return avatarContainer;
  }

  /**
   * Creates the main content container for the popover.
   * @returns {UIDiv} The popover content element.
   * @private
   */
  #createPopoverContent() {
    const content = new UIDiv();
    content.addClass('py-2 min-w-80 max-w-96');

    // Add search bar
    content.add(this.#createSearchBar());
    content.add(this.#createSeparator());

    // Add user list
    const userList = this.#createUserList();
    content.add(userList);

    return content;
  }

  /**
   * Creates a search bar for filtering users
   * @returns {UIDiv} The search bar element
   * @private
   */
  #createSearchBar() {
    const search = new Search({
      handleSearch: searchValue => {
        const searchTerm = searchValue.toLowerCase();
        this.#filterUsers(searchTerm);
        this.#updateUserList();
      },
      className: 'mb-2 mx-3',
      inputId: `people-search-input-${this.item?.id || 'bar'}`,
      placeholder: i18next.t('pages.projects.cells.people.searchUsers'),
    });

    return search;
  }

  /**
   * Creates the user list container
   * @returns {UIDiv} The user list element
   * @private
   */
  #createUserList() {
    const listContainer = new UIDiv();
    listContainer.addClass('max-h-60 overflow-y-auto');
    this.userListContainer = listContainer;

    this.#updateUserList();
    return listContainer;
  }

  /**
   * Updates the user list based on current filtered users
   * @private
   */
  #updateUserList() {
    if (!this.userListContainer) return;

    this.userListContainer.clear();

    if (this.filteredUsers.length === 0) {
      const emptyState = new UIDiv();
      emptyState.addClass('px-3 py-4 text-sm text-gray-500 text-center');
      emptyState.setTextContent('No users found');
      this.userListContainer.add(emptyState);
      return;
    }

    this.filteredUsers.forEach(user => {
      const userItem = this.#createUserItem(user);
      this.userListContainer.add(userItem);
    });
  }

  /**
   * Creates a single user item for the list
   * @param {Object} user - The user object
   * @returns {UIDiv} The user item element
   * @private
   */
  #createUserItem(user) {
    const isSelected = this.selectedUsers.some(u => u.uid === user.uid);

    const userItem = new UIDiv();
    userItem.addClass(
      'px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-3 group focus-visible:outline-none focus-visible:bg-gray-100',
    );

    if (isSelected) {
      userItem.addClass('bg-blue-50 hover:bg-blue-100');
    }

    // User avatar
    const avatarContainer = new UIDiv();
    avatarContainer.addClass(
      'w-8 h-8 rounded-full flex-shrink-0 overflow-hidden',
    );

    if (user.img_url) {
      const avatar = new UIImage(user.img_url);
      avatar.addClass('w-full h-full object-cover');
      avatarContainer.add(avatar);
    } else {
      avatarContainer.addClass('bg-gray-200 flex items-center justify-center');
      const initials = user.full_name
        ? user.full_name.charAt(0).toUpperCase()
        : '?';
      const initialsText = new UIText(initials);
      initialsText.addClass('text-xs font-medium text-gray-600');
      avatarContainer.add(initialsText);
    }

    // User name
    const userName = new UIText(user.full_name || user.email || 'Unknown');
    userName.addClass('truncate flex-1 font-medium text-gray-900');

    // Remove/Cross icon (only show for selected users)
    const removeIcon = new UIDiv();
    if (isSelected) {
      removeIcon.addClass(
        'w-5 h-5 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center text-red-600 hover:text-red-700 transition-colors font-bold text-sm flex-shrink-0',
      );
      removeIcon.setTextContent('×');
      removeIcon.onClick(e => {
        e.stopPropagation(); // Prevent the main click handler
        this.#toggleUser(user);
      });
    } else {
      removeIcon.addClass('w-5 h-5 flex-shrink-0'); // Empty space to maintain alignment
    }

    userItem.add(avatarContainer, userName, removeIcon);
    userItem.setAttribute('tabindex', '0');
    userItem.onKeyDown(event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.#toggleUser(user);
      }
    });

    userItem.onClick(e => {
      e.stopPropagation(); // Prevent popover from closing immediately
      this.#toggleUser(user);
    });

    return userItem;
  }

  /**
   * Filters users based on search term
   * @param {string} searchTerm - The search term
   * @private
   */
  #filterUsers(searchTerm) {
    if (!searchTerm.trim()) {
      this.filteredUsers = [...this.users];
    } else {
      this.filteredUsers = this.users.filter(
        user =>
          (user.full_name || '').toLowerCase().includes(searchTerm) ||
          (user.email || '').toLowerCase().includes(searchTerm),
      );
    }
  }

  /**
   * Updates the trigger element dynamically without recreating the popover
   * @private
   */
  #updateTrigger() {
    if (!this.currentTrigger) return;

    // Create new trigger content
    const newTrigger =
      this.selectedUsers.length === 0
        ? this.#createEmptyTrigger()
        : this.#createSelectedTrigger();

    // Replace the content of the current trigger
    this.currentTrigger.clear();
    newTrigger.getChildren().forEach(child => {
      const childUI = new UIElement(child);
      this.currentTrigger.add(childUI);
    });

    // Update the classes to match the new trigger type
    this.currentTrigger.dom.className = newTrigger.dom.className;
  }

  /**
   * Toggles user selection
   * @param {Object} user - The user to toggle
   * @private
   */
  #toggleUser(user) {
    const isSelected = this.selectedUsers.some(u => u.uid === user.uid);

    if (isSelected) {
      this.selectedUsers = this.selectedUsers.filter(u => u.uid !== user.uid);
    } else {
      this.selectedUsers.push(user);
    }

    // Update both popover content and trigger
    this.#updateUserList();
    this.#updateTrigger();
    this.#saveSelection();
  }

  /**
   * Removes a user from selection
   * @param {string} userId - The user ID to remove
   * @private
   */
  #removeUser(userId) {
    this.selectedUsers = this.selectedUsers.filter(u => u.uid !== userId);

    // Update both popover content and trigger
    this.#updateUserList();
    this.#updateTrigger();
    this.#saveSelection();
  }

  /**
   * Saves the current selection
   * @private
   */
  async #saveSelection() {
    try {
      const selectedUserIds = this.selectedUsers.map(user => user.uid);
      const newValue = JSON.stringify(selectedUserIds);

      if (newValue === this.value) return;

      // If onValueChange callback is provided, use it instead of API call
      if (this.onValueChange) {
        this.onValueChange(selectedUserIds);
        // Update the local value
        this.value = newValue;
      } else {
        // Update the value via API
        const entityType = !this.item.reconstructions ? 'folder' : 'file';
        const entityId = this.item.id || this.item.sharingId;

        await updateColumnValue(this.column.id, entityType, entityId, newValue);

        // Update the local value
        this.value = newValue;
        if (this.item) {
          this.item[this.column.key] = newValue;
        }
      }

      // Don't re-render here since we're updating dynamically
    } catch (error) {
      console.error('Failed to update people assignment:', error);
      toast.error('Failed to update people assignment');
    }
  }

  /**
   * Creates a separator line
   * @returns {UIDiv} The separator element
   * @private
   */
  #createSeparator() {
    const separator = new UIDiv();
    separator.addClass('border-t border-gray-200 my-1');
    return separator;
  }

  /**
   * Renders the uneditable version of the cell
   * @private
   */
  #renderUneditableInput() {
    const container = new UIDiv();
    container.addClass('min-h-[34px] flex items-center gap-1');
    if (this.selectedUsers.length > 0) {
      // Show first 3 user avatars
      this.selectedUsers.slice(0, 3).forEach(user => {
        const avatar = this.#createUserAvatar(user, false); // false for non-removable
        container.add(avatar);
      });

      // Show "+X more" indicator if there are more than 3 users
      if (this.selectedUsers.length > 3) {
        const moreIndicator = new UIDiv();
        moreIndicator.addClass(
          'w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-600 font-medium',
        );
        moreIndicator.setTextContent(`+${this.selectedUsers.length - 3}`);
        container.add(moreIndicator);
      }
    }

    this.add(container);
  }

  /**
   * Toggles edit mode
   * @param {boolean} isEditMode - Whether to enter edit mode
   */
  toggleEditMode(isEditMode) {
    this.canEdit = isEditMode;
    this.isEditMode = isEditMode;
    if (isEditMode) {
      this.render(); // Re-render to show editable state
    } else {
      this.clear(); // Clear current content
      this.#renderUneditableInput(); // Show uneditable state
    }
  }

  /**
   * Gets the current value of the cell
   * @returns Array object of {name: string, id: string}
   * This getter is adapted to the comment edition context
   */
  getValue() {
    return this.selectedUsers.map(user => ({
      name: user.full_name || user.email,
      id: user.uid,
    }));
  }
}
